import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ALL_VIEW_ID, RECORD_TILE_PARAM, Color, emptySelection, listExportPath, listSelectionPath, listSelectionQuery, type ListSelectionAnswer, type Selection, ITEM_SERIES_MANUFACTURER_PARAM, ITEM_SERIES_PARAM, LIST_CONDITIONS_PARAM, LIST_PAGES, formatListConditions, parseListConditions, type CriteriaRow, MERGE_SET_CAP, NEW_DOCUMENT_ID, mergeSetCapSentence, SAVED_VIEW_PANE_PARAM, SAVED_VIEW_PARAM, Size, TAG_FILTER_PARAM, builtInCriteria, builtInNarrows, builtInViews, formatTagFilter, isEmptyTagFilter, parseTagFilter, isBulkEditRecordType, isMappable, listScopeKey, listUrlFilterValue, recordHref, viewColumns, type ColumnDef, type ListPageSpec, type PageSize, type ItemSeriesOption, type SortState, RECORD_ICONS } from '@bms2/shared';
import { Button, SegmentedTab } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { readSavedLayout, t } from '@bms2/shared';
import { BulkEditModal } from './BulkEditModal';
import { MergeSetWindow, mergeSetNotice, type MergeSetNotice } from '../duplicates/MergeSetWindow';
import { VcardImportModal } from './VcardImportModal';
import { ListViewer, switcherView, type ListView } from '../../components/composites/ListViewer';
import { SavedViewAddress, SavedViewWindow, useSavedViews } from '../../components/composites/savedViews';
import { QuickViewBar } from '../../components/composites/QuickViewBar';
import { TagFilterMenu, tagFilterGroupsFor, tagFilterSummary, useTagFilterOptions } from '../../components/composites/TagFilterMenu';
import { ITEM_SERIES_PATH, ItemSeriesFilter, type ItemSeriesChoice } from './ItemSeriesFilter';
import { ItemFilter, useCriteriaAttributes } from './ItemFilterWindow';
import { ExportLookupRequestButton, ImportLookupBundleButton, type ResolvedSelection } from './ItemLookupActs';
import { Icon } from '../../components/Icon';
import { AccessDenied } from '../../shell/Fallbacks';
import { PageModule } from '../../shell/PageModule';
import { useSettingsGear } from '../settings/SettingsGear';
import { useStatusResolver } from '../../shell/StatusResolver';
import { useViewerZone } from '../../shell/ViewerZone';
import { PAGES_PATH, RECORD_WRITTEN_EVENT, SAVED_VIEWS_PATH, apiFetch, post, useApi } from './api';
import { downloadThroughFrame } from '../reports/api';
import listStyles from './ListPage.module.css';
import { NewRecordModal } from './NewRecordModal';
import { SiteModal } from './SiteModal';
import { IssueStockModal, NewRevaluationModal, ReturnStockModal } from './StockDocumentPages';
import { VendorCreditModal } from './VendorCreditModal';
import { TextedCardOffers } from './TextedCardOffers';
import { NewBomModal } from './NewBomModal';
import { PipelineBoard } from './PipelineBoard';
import { MapView } from './MapView';
import paneStyles from './ListPane.module.css';
import type { IconMeaning } from '@bms2/shared';
import { put } from './api';
import { ListRowMarks } from '../../components/composites/ListRowMarks';

/**
 * The List Page archetype over the List Viewer (piece 13). One construction
 * serves all four records, because the columns and the record route come from
 * the shared register rather than from the screen. 🔨 The banner carries the
 * New button and no status pill; the Page Module's own shape check refuses the
 * pair rather than rendering it.
 *
 * 🔒 The + New button is drawn only where something answers it (piece 13). A
 * record with no field form, no page of its own and no bespoke modal is raised
 * from another document, so its list carries no + New at all; drawing one opened
 * an empty modal that could write nothing (found in review, 2026-09-15).
 */
export interface ListPageProps {
  spec: ListPageSpec;
  apiBaseUrl?: string;
  onNew?: () => void;
  /** 'pane' draws the list as the left pane of a list-and-detail page: no page module of its own, the first two columns, the active row marked (decision 0318). */
  frame?: 'page' | 'pane';
  activeId?: string | null;
  /** The pane's collapse control, pinned in its head; absent where the list is not in a pane (decision 0408). */
  onCollapsePane?: () => void;
  /**
   * 🔒 Told when the server refuses this list's read, where the list is a pane
   * beside a record (T218; decision 0693). A pane that is told draws nothing and
   * leaves the frame to withhold it; a list on its own page still reads Access
   * Denied.
   */
  onRefused?: () => void;
}

interface ListAnswer {
  columns: string[];
  rows: Record<string, string | number | null>[];
  /** 🔒 The whole matching set's size and the page the rows are, which the server cuts (decision 1890). */
  total?: number;
  page?: number;
  /** Edit Selected, gated as each list's single edit (decisions 0982, 2890); Import price list lives in Settings (decision 0342); Import vCard on the People list, gated on write on Person (decision 0341); + New on an accounting document's list, gated on the document write gate (decision 0687). */
  actions?: { importVcard?: boolean; issueStock?: boolean; returnStock?: boolean; create?: boolean; bulkEdit?: boolean; merge?: boolean };
}

/**
 * 🔒 Whether a write the frame announced can have changed a list's rows (T641;
 * decision 1431). A write reaches the list when its path sits under the list's
 * own route, which is its documents' route too, or when it writes a record of
 * the list's type through the generic record routes. A note, a tag or a flag on
 * a record writes under its own route and reaches no list. 🔒 A suggestion's
 * Accept, Dismiss or Same Company As writes under the record's checks route,
 * and reaches the list of its type, because the gold dot a person row or a company
 * row carries is read from those checks (T3028; T3057; T3066).
 */
export function writeReachesList(written: string, list: { recordType: string; route: string }): boolean {
  const segments = (written.split('?')[0] ?? '').split('/').filter(Boolean);
  const own = list.route.split('/').filter(Boolean);
  if (own.length > 0 && own.every((segment, index) => segments[index] === segment)) return true;
  if (segments[0] === 'records' && segments[1] === list.recordType && segments[3] === 'checks') return true;
  if (`/${segments[0] ?? ''}` !== PAGES_PATH) return false;
  return segments[1] === list.recordType || (segments[1] === 'records' && segments[2] === list.recordType);
}

/**
 * 🔒 The inputs of a list that ride in its address across an open and a Back (decision 7750; T3123):
 * the search, the plain tag, the Tags filter, the list's own filter and the chosen saved view. The
 * saved view is `savedView` on the page and `paneView` in the pane (T1411), so it is rewritten to the
 * form of the frame the address is going to and never to `view`, which is the board-or-list choice.
 * Carried whole, the list beside the record reads what it read before the open and the page after
 * the Back reads what the pane did.
 */
export function listInputsQuery(spec: ListPageSpec, params: URLSearchParams, into: 'pane' | 'page'): string {
  const next = new URLSearchParams();
  for (const key of ['q', 'tag']) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  const tagText = spec.tagFilter === true ? params.get(TAG_FILTER_PARAM) : null;
  if (tagText) next.set(TAG_FILTER_PARAM, tagText);
  const filterValue = listUrlFilterValue(spec.urlFilter, spec.urlFilter ? params.get(spec.urlFilter.param) : null);
  if (spec.urlFilter && filterValue) next.set(spec.urlFilter.param, filterValue);
  // 🔒 And the Items list's Series choice, which the pane reads as the page does (decision 7871; T3142).
  if (spec.seriesFilter === true) {
    for (const key of [ITEM_SERIES_PARAM, ITEM_SERIES_MANUFACTURER_PARAM]) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
  }
  // 🔒 And the list's segment, the Sites list's site type, which the pane reads as the page does (decision 10100).
  const segment = spec.segmentFilter ? params.get(spec.segmentFilter.param) : null;
  if (spec.segmentFilter && segment) next.set(spec.segmentFilter.param, segment);
  // 🔒 And the Items list's Filter conditions, so the list beside the record and the list after Back read them (decision 8800).
  const conditions = spec.conditionsFilter === true ? params.get(LIST_CONDITIONS_PARAM) : null;
  if (conditions) next.set(LIST_CONDITIONS_PARAM, conditions);
  const savedView = params.get(SAVED_VIEW_PANE_PARAM) ?? params.get(SAVED_VIEW_PARAM);
  if (savedView) next.set(into === 'pane' ? SAVED_VIEW_PANE_PARAM : SAVED_VIEW_PARAM, savedView);
  const text = next.toString();
  return text ? `?${text}` : '';
}

/** The layouts a list page remembers: the Deals list has a board and the Locations and Sites lists a map (decisions 0206, 0343). */
type Layout = 'list' | 'board' | 'map';

export function ListPage({ spec, apiBaseUrl = '', onNew, frame = 'page', activeId = null, onCollapsePane, onRefused }: ListPageProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const term = params.get('q') ?? '';
  /* 🔒 The gear to the settings that govern this list, on its own page alone: a pane has no title bar of the page module's (decisions 2805, 2815; T1614). */
  const gear = useSettingsGear(frame === 'page' ? spec.settingsEntries : undefined);
  /** The board-or-list choice on the Deals list and the map-or-list choice on the Locations and Sites lists, remembered per person as a SavedView row (piece 12; decisions 0206, 0343). */
  const boardable = spec.boardable === true;
  const mappable = isMappable(spec.recordType);
  const layouts: readonly Layout[] = boardable ? ['list', 'board'] : mappable ? ['list', 'map'] : ['list'];
  const preference = useApi<{ layout: unknown }>(layouts.length > 1 ? `${SAVED_VIEWS_PATH}/layout?scopeKey=${encodeURIComponent(listScopeKey(spec.route))}` : null, apiBaseUrl);
  const remembered = preference.state.kind === 'ready' ? readSavedLayout(preference.state.data.layout).content ?? null : null;
  const asked = params.get('view') ?? remembered ?? 'list';
  // A remembered layout this list does not have, such as a board on a Locations list, reads as the list.
  const view: Layout = (layouts as readonly string[]).includes(asked) ? (asked as Layout) : 'list';
  const chooseView = (content: Layout) => {
    const next = new URLSearchParams(params);
    next.set('view', content);
    setParams(next);
    void put(SAVED_VIEWS_PATH, { scopeKey: listScopeKey(spec.route), layout: { content } }, apiBaseUrl);
  };
  const tag = params.get('tag') ?? '';
  /**
   * 🔒 The list's own filter, carried by its address (decision 0762; T123): the
   * Payments list is read as Payments received or Payments made. It rides with the
   * read, with + New and with every row opened, so the pick keeps the list it came from.
   */
  const urlFilterValue = listUrlFilterValue(spec.urlFilter, spec.urlFilter ? params.get(spec.urlFilter.param) : null);
  const urlFilterQuery = spec.urlFilter && urlFilterValue ? `?${new URLSearchParams({ [spec.urlFilter.param]: urlFilterValue }).toString()}` : '';
  const urlFilterTitle = spec.urlFilter?.values.find((value) => value.value === urlFilterValue)?.title ?? null;
  /**
   * 🔒 The Items list's Series menu (decision 7640; T3125): the address carries the
   * choice, so a reload keeps it, and it narrows the server's read, so the count
   * and the pages are the filtered set's.
   */
  // 🔒 The choice is read in the pane as well as on the page, so the list beside a record keeps its rows; the menu and its options are the page's alone (decision 7871).
  const seriesFiltered = spec.seriesFilter === true;
  const seriesChoice = { series: seriesFiltered ? params.get(ITEM_SERIES_PARAM) ?? '' : '', manufacturer: seriesFiltered ? params.get(ITEM_SERIES_MANUFACTURER_PARAM) ?? '' : '' };
  const seriesOptions = useApi<{ rows?: ItemSeriesOption[] }>(seriesFiltered && frame === 'page' ? ITEM_SERIES_PATH : null, apiBaseUrl);
  const writeSeries = (next: ItemSeriesChoice) => {
    const query = new URLSearchParams(params);
    for (const [key, value] of [[ITEM_SERIES_PARAM, next.series], [ITEM_SERIES_MANUFACTURER_PARAM, next.manufacturer]] as const) {
      if (value === '') query.delete(key);
      else query.set(key, value);
    }
    setParams(query);
  };
  /**
   * 🔒 The Items list's Filter (decision 8800; T3276): several conditions at once, attribute values
   * among them, carried by the address under `conditions` and read in the pane as on the page, so a
   * reload, an open and its Back keep them. The read sends them inside its criteria, after the view's own.
   */
  const conditionsFiltered = spec.conditionsFilter === true;
  const conditionsText = conditionsFiltered ? params.get(LIST_CONDITIONS_PARAM) ?? '' : '';
  const conditions = useMemo(() => parseListConditions(conditionsText), [conditionsText]);
  const writeConditions = useCallback(
    (rows: readonly CriteriaRow[]) =>
      setParams((was) => {
        const next = new URLSearchParams(was);
        const text = formatListConditions(rows);
        if (text === '') next.delete(LIST_CONDITIONS_PARAM);
        else next.set(LIST_CONDITIONS_PARAM, text);
        return next;
      }),
    [setParams],
  );
  /**
   * 🔒 The list's segment row (decision 10100; T3476): All, then each value its route answers. The address carries the
   * pick, so a reload, an open and its Back keep it, and the read narrows on it through its criteria, after the view's
   * own. 🔨 Read in the pane as on the page; the row itself is the page's alone.
   */
  const segmentFilter = spec.segmentFilter ?? null;
  const segmentValue = segmentFilter ? params.get(segmentFilter.param) ?? '' : '';
  const segmentOptions = useApi<{ rows?: { value: string; label: string }[] }>(segmentFilter && frame === 'page' ? segmentFilter.optionsPath : null, apiBaseUrl);
  const segmentCriteria: CriteriaRow[] = segmentFilter && segmentValue ? [{ id: 'segment', field: segmentFilter.column, comparator: 'is', value: segmentValue }] : [];
  const writeSegment = (value: string) =>
    setParams((was) => {
      const next = new URLSearchParams(was);
      if (!segmentFilter) return next;
      if (value === '') next.delete(segmentFilter.param);
      else next.set(segmentFilter.param, value);
      return next;
    });
  const [filtering, setFiltering] = useState(false);
  /**
   * 🔒 T3289: on the Items list a Filter, a Series choice or a search that matches nothing says so and offers Clear Filter, which
   * takes all three off together, in place of the empty catalog's invitation (piece 13).
   */
  const filterActive = (conditionsFiltered || seriesFiltered) && (conditions.length > 0 || seriesChoice.series !== '' || seriesChoice.manufacturer !== '' || term !== '');
  const clearFilters = () =>
    setParams((was) => {
      const next = new URLSearchParams(was);
      for (const key of [LIST_CONDITIONS_PARAM, ITEM_SERIES_PARAM, ITEM_SERIES_MANUFACTURER_PARAM, 'q']) next.delete(key);
      return next;
    });
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [mergeRefusal, setMergeRefusal] = useState<string | null>(null);
  /** 🔒 The selection as the viewer holds it and how many rows it names (decision 7780): after Select all N matching it is the read's filter less the rows unticked. */
  const [selection, setSelection] = useState<{ value: Selection; count: number }>({ value: emptySelection, count: 0 });
  /** 🔒 A new selection drops the refusal the last Merge press answered, which named the selection before it (T3168). */
  const keepSelection = useCallback((value: Selection, count: number) => {
    setSelection({ value, count });
    setMergeRefusal(null);
  }, [setMergeRefusal]);
  const [bulkEditing, setBulkEditing] = useState(false);
  /** 🔒 The list's Merge (decision 7450): the selection it opened on, held while the window is open, and the notice its press answers with. */
  const [merging, setMerging] = useState<readonly string[] | null>(null);
  const [mergeNotice, setMergeNotice] = useState<MergeSetNotice | null>(null);
  const [importingVcards, setImportingVcards] = useState(false);
  /** 🔒 Issue Stock from a stock list (decision 1142), unmounted when closed so a second issue opens on empty fields. */
  const [issuing, setIssuing] = useState(false);
  const [returning, setReturning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>({ key: spec.defaultSortKey, direction: 'asc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [creating, setCreating] = useState(false);
  /**
   * 🔒 The Tags menu by category, then by tag (T1581, screen E), on a list that
   * declares it, the Deals list first. The address carries the filter, so a
   * reload, Back and a copied link keep it, and a saved view keeps it too. 🔒 The
   * filter is read in the pane as well as on the page and the menu is drawn on the
   * page alone, so the list beside a record keeps the rows it had (decision 7750).
   */
  const tagFiltered = spec.tagFilter === true;
  const tagFilterText = tagFiltered ? params.get(TAG_FILTER_PARAM) ?? '' : '';
  const tagFilter = useMemo(() => parseTagFilter(tagFilterText), [tagFilterText]);
  // 🔒 The options are read on the page, and in the pane only while a filter stands for its line to name (T3162): a pane with no filter draws no menu and reads none.
  const tagOptions = useTagFilterOptions(apiBaseUrl, tagFiltered && (frame === 'page' || !isEmptyTagFilter(tagFilter)));
  const tagGroups = useMemo(() => tagFilterGroupsFor(tagOptions, spec.recordType, tagFilter), [tagOptions, spec.recordType, tagFilter]);
  const writeTagFilter = useCallback(
    (text: string) =>
      setParams(
        (was) => {
          const next = new URLSearchParams(was);
          if (text === '') next.delete(TAG_FILTER_PARAM);
          else next.set(TAG_FILTER_PARAM, text);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  // 🔒 A view opening as the page loads leaves a filter the address already carries, so a reload keeps what was ticked after the view was chosen.
  const tagViewApplied = useRef(false);
  /**
   * 🔨 The views are the person's own `SavedView` rows on this list's scope,
   * read from the one preference store (piece 12). 💰 The modal's Save appended
   * a name to a list held in memory, applied nothing and survived nothing
   * (found while building, 2026-09-09). 🔒 The list opens on the person's
   * default view, the chosen view rides in the address, and choosing one
   * restores its sort and rows per page (decisions 2171, 2174).
   */
  const scopeKey = listScopeKey(spec.route);
  /** 🔒 The list's built-in views from its register, All first (decision 2880): each reads what the list's read already takes. */
  const builtIns = useMemo(() => builtInViews(spec), [spec]);
  const store = useSavedViews({
    scopeKey,
    apiBaseUrl,
    builtIns,
    onApply: (saved) => {
      if (saved.sort) setSort(saved.sort);
      if (saved.pageSize) setPageSize(saved.pageSize);
      if (tagFiltered) {
        const first = !tagViewApplied.current;
        tagViewApplied.current = true;
        if (!(first && tagFilterText !== '')) writeTagFilter(saved.tagFilter ?? '');
      }
    },
  });
  /** 🔒 The switcher lists every view, built-in and own, with each star as it stands for this person (decisions 2171, 2880); a built-in view edits its columns alone (decision 2981). */
  const ownById = new Map(store.views.map((view) => [view.id, view]));
  const views: ListView[] = store.switcher.map((entry) => {
    const own = ownById.get(entry.id);
    if (own) return { ...switcherView(own), isFavourite: entry.starred };
    const builtIn = builtIns.find((view) => view.key === entry.id);
    // 🔒 A built-in view carries the pencil too: its columns are the person's own (decision 2981).
    return { id: entry.id, name: entry.name, isFavourite: entry.starred, editable: true, builtIn: true, narrows: builtIn ? builtInNarrows(builtIn) : false };
  });
  const current = store.current;
  const builtIn = store.currentBuiltIn;
  const viewerZone = useViewerZone();
  /** 🔒 The catalog's attribute types for the condition rows, read only while the Filter or the New View window is open on the Items list (decision 8800). */
  const criteriaAttributes = useCriteriaAttributes(apiBaseUrl, conditionsFiltered && frame === 'page' && (filtering || store.window !== null));

  /**
   * 🔒 The server cuts the page (decision 1890; T1250): the read names the page,
   * its size, the sort and the saved view's criteria, and the answer carries the
   * whole matching set's count. 💰 The list read 200 rows and paged over those,
   * so a catalog of 312 items showed 200 and said nothing (product owner,
   * 2026-10-04). Any change to what is read starts again on page one.
   */
  // 🔒 A built-in view reads through the same criteria path a person's own view does (decision 2880).
  const viewCriteria = builtIn ? builtInCriteria(builtIn) : current?.criteria ?? [];
  // 🔒 The Filter's conditions narrow the read with the view's, every one of both to hold (decision 8800).
  const readCriteria = [...viewCriteria, ...conditions, ...segmentCriteria];
  const criteria = readCriteria.length > 0 ? JSON.stringify(readCriteria.map(({ field, comparator, value }) => ({ field, comparator, value }))) : '';
  const scope = ((): string => {
    const query = new URLSearchParams();
    if (term) query.set('q', term);
    if (tag) query.set('tag', tag);
    if (tagFiltered && !isEmptyTagFilter(tagFilter)) query.set(TAG_FILTER_PARAM, formatTagFilter(tagFilter));
    // 🔒 And with the read's own parameters where it names them: a kind, a held or unrouted lead, the Off-default accounts (decisions 0212, 1486, 2880).
    for (const [key, value] of Object.entries(builtIn?.filter ?? {})) query.set(key, value);
    if (spec.urlFilter && urlFilterValue) query.set(spec.urlFilter.param, urlFilterValue);
    if (seriesChoice.series !== '') {
      query.set(ITEM_SERIES_PARAM, seriesChoice.series);
      if (seriesChoice.manufacturer !== '') query.set(ITEM_SERIES_MANUFACTURER_PARAM, seriesChoice.manufacturer);
    }
    query.set('pageSize', String(pageSize));
    if (sort) {
      query.set('sort', sort.key);
      query.set('sortDirection', sort.direction);
    }
    if (criteria) query.set('criteria', criteria);
    // 🔒 A date matched by is on reads the day in the viewer's zone (decision 2172).
    if (criteria) query.set('zone', viewerZone);
    return query.toString();
  })();
  const readKey = `${spec.recordType}?${scope}`;
  /** 🔒 The read without its page, size or sort: what a filter selection carries, and what clears the selection when it changes (decision 7780). */
  const selectionQuery = listSelectionQuery(scope);
  const [paged, setPaged] = useState<{ scope: string; page: number }>({ scope: readKey, page: 1 });
  const page = paged.scope === readKey ? paged.page : 1;
  const path = `${PAGES_PATH}/list/${spec.recordType}?${scope}&page=${page}`;
  /**
   * 🔨 A board or a map fetches no list (T640; decision 1431): the board and the
   * map read their own data, so the list's path is empty while either is shown.
   * A remembered layout still being read holds the list too, or the Deals list
   * would read its rows behind a board it was about to draw.
   */
  // 🔒 The person's views are read before the list, so a default view opens without a first read of All behind it (decision 2171).
  const choosing = (layouts.length > 1 && !params.get('view') && preference.state.kind === 'loading') || !store.settled;
  const listShown = !choosing && !(boardable && view === 'board') && !(mappable && view === 'map');
  const { state, reload, denied } = useApi<ListAnswer>(listShown ? path : null, apiBaseUrl);
  /**
   * 🔒 A write the server accepted reads the rows again, so a status a record page moved is the status the list draws (T281).
   * 🔒 Only a write that can change these rows does (T641; decision 1431): one to this list's record type or route. Writes
   * that arrive together read the rows once, on the next task.
   */
  const recordType = spec.recordType;
  const listRoute = spec.route;
  useEffect(() => {
    let queued: ReturnType<typeof setTimeout> | null = null;
    const onWritten = (event: Event) => {
      const written = (event as CustomEvent<{ path?: unknown }>).detail?.path;
      if (typeof written === 'string' && !writeReachesList(written, { recordType, route: listRoute })) return;
      if (queued !== null) return;
      queued = setTimeout(() => {
        queued = null;
        reload();
      }, 0);
    };
    window.addEventListener(RECORD_WRITTEN_EVENT, onWritten);
    return () => {
      window.removeEventListener(RECORD_WRITTEN_EVENT, onWritten);
      if (queued !== null) clearTimeout(queued);
    };
  }, [reload, recordType, listRoute]);
  const paneRefused = denied && frame === 'pane' && onRefused !== undefined;
  useEffect(() => {
    if (paneRefused) onRefused?.();
  }, [paneRefused, onRefused]);
  const allRows = state.kind === 'ready' ? state.data.rows : [];
  const actions = state.kind === 'ready' ? state.data.actions : undefined;
  // 🔒 Edit selected is drawn where the list declares its fields and the API's gate answers true for this viewer (decision 0982).
  const offersBulkEdit = (spec.bulkEdit?.length ?? 0) > 0 && actions?.bulkEdit === true;
  // 🔒 Merge on the Companies, People and Items lists is drawn where the API answers that this viewer's press could pass (decisions 7450, 7470).
  const mergeType = spec.recordType === 'CompanyRecord' || spec.recordType === 'PersonRecord' || spec.recordType === 'Item' ? spec.recordType : null;
  const offersMerge = mergeType !== null && actions?.merge === true;
  /**
   * 🔒 A filter selection reaches an action as the read's query and the rows unticked, never as every id (decisions 7780, 7870).
   * Merge resolves its ids through the list's selection route, since its window names each record; above its cap it never asks.
   * Edit Selected sends the selection itself, and its service resolves it.
   */
  const filterSelected = selection.value.mode === 'filter';
  const selectedCount = filterSelected ? selection.count : selected.length;
  /** 🔒 Export Lookup Request on the Items list's page, for any reader of the list (decision 12660): it writes nothing. */
  const offersLookupRequest = spec.recordType === 'Item' && frame === 'page';
  /** 🔒 Import Lookup Bundle for a writer of Items on the list's page (decision 12661). */
  const offersLookupBundle = offersLookupRequest && actions?.create !== false;
  /** The ticked rows as ids: a filter selection resolved through the list's own selection route, as Merge resolves it (decision 7870). */
  const resolveSelected = async (): Promise<ResolvedSelection> => {
    if (selection.value.mode !== 'filter') return { ok: true, ids: [...selected] };
    const answer = await post<ListSelectionAnswer>(listSelectionPath(spec.recordType), { query: selectionQuery, exclusions: [...selection.value.exclusions] }, apiBaseUrl);
    return answer.ok ? { ok: true, ids: answer.data.ids } : { ok: false, message: answer.message };
  };
  /** 🔒 Edit Selected over Select all N matching sends the read's query and the rows unticked, and the server resolves them (decision 7870). */
  const bulkSelection = selection.value.mode === 'filter' ? { value: { query: selectionQuery, exclusions: [...selection.value.exclusions] }, count: selection.count } : null;
  /** 🔒 One resolution at a time: a second press while the selection is read is ignored and the button stands busy (T3168). */
  const [resolvingMerge, setResolvingMerge] = useState(false);
  const mergeInFlight = useRef(false);
  const startMerge = async () => {
    if (selection.value.mode !== 'filter') return setMerging([...selected]);
    if (mergeInFlight.current) return;
    mergeInFlight.current = true;
    setResolvingMerge(true);
    // The flag clears whether the read answers or throws, so a failed press never leaves Merge busy.
    let answer: Awaited<ReturnType<typeof post<ListSelectionAnswer>>>;
    try {
      answer = await post<ListSelectionAnswer>(listSelectionPath(spec.recordType), { query: selectionQuery, exclusions: [...selection.value.exclusions] }, apiBaseUrl);
    } finally {
      mergeInFlight.current = false;
      setResolvingMerge(false);
    }
    if (!answer.ok) return setMergeRefusal(answer.message);
    setMergeRefusal(null);
    setMerging(answer.data.ids);
  };
  // The view's criteria narrow the rows on the server (decision 1890) and its columns narrow the table, in the view's order with the first column leading (decision 2173).
  const rows = allRows;
  // 🔒 A built-in view draws the columns on the person's star row where they have arranged them (decision 2981).
  const chosenColumns = useMemo(() => viewColumns(spec.columns, store.columnKeys), [spec.columns, store.columnKeys]);
  /** 🔒 Export writes the list as it stands: its filters and its visible columns, read under the viewer's own scope (decision 10940; T1051). */
  const [exportRefusal, setExportRefusal] = useState<string | null>(null);
  const exportList = async () => {
    setExportRefusal(null);
    const keys = chosenColumns.map((column) => column.key).join(',');
    const saved = await downloadThroughFrame(apiFetch, `${apiBaseUrl}${listExportPath(spec.recordType)}?${selectionQuery}&columns=${encodeURIComponent(keys)}`, `${spec.recordType}.csv`);
    if (!saved) setExportRefusal(t('list.export.failed'));
  };
  const offersExport = frame === 'page' && view === 'list';
  // 🔒 A pane keeps the identifying pair, then the status and the total beside it, and the detail carries the rest (decisions 0318, 0639).
  const columns = useMemo(() => (frame === 'pane' ? paneColumns(chosenColumns) : chosenColumns), [frame, chosenColumns]);
  // 🔒 Every status column draws its pill through the one resolver, by the enum the register names for it (decision 0639).
  const resolveStatus = useStatusResolver();
  const status = useMemo(() => ({ resolve: resolveStatus, entityTypeFor: (key: string) => spec.columns.find((column) => column.key === key)?.statusEntity ?? '' }), [resolveStatus, spec.columns]);
  // While the remembered layout is read, the list is still loading, never empty.
  const viewerState = choosing ? ({ kind: 'loading' } as const) : state.kind === 'ready' && rows.length === 0 ? ({ kind: 'empty' } as const) : state;

  // 🔒 A row opens with the list's inputs in the address, so the list beside the record reads what it read (decision 7750; T3123).
  const open = useCallback(
    (row: Record<string, string | number | null>) => navigate(`${recordHref(spec.recordType, String(row.id))}${listInputsQuery(spec, params, 'pane')}`),
    [navigate, spec, params],
  );
  /** 🔒 A Marks triangle opens the record at the tile that puts it right (decision 9720; T3164), the list kept beside it as a row's open keeps it. */
  const openAtTile = useCallback(
    (row: Record<string, unknown>, tile: string) => {
      const query = new URLSearchParams(listInputsQuery(spec, params, 'pane').replace(/^\?/, ''));
      query.set(RECORD_TILE_PARAM, tile);
      navigate(`${recordHref(spec.recordType, String(row.id))}?${query.toString()}`);
    },
    [navigate, spec, params],
  );
  /** 🔒 Where the list's scroll box stood, held across the frame change that remounts the viewer and drawn again after it (decision 7750). */
  const scrollTop = useRef(0);
  const keepScroll = useCallback((top: number) => {
    scrollTop.current = top;
  }, []);

  const opened = (created: { id: string; route?: string }) => {
    setCreating(false);
    reload();
    // 🔒 A save that opens another page names it: the Parts form opens its Quote's (decision 2290).
    navigate(created.route ?? recordHref(spec.recordType, created.id));
  };

  const label = urlFilterTitle ?? spec.label;
  // 🔒 A built-in view other than All names itself after the list's title, as the queues did (decisions 1486, 2880).
  const titleSuffix = builtIn && builtIn.key !== ALL_VIEW_ID ? builtIn.name : null;
  const title = titleSuffix ? `${label} · ${titleSuffix}` : label;
  const tagLine = tagFiltered ? tagFilterSummary(tagGroups, tagFilter) : '';
  const subtitle = [term ? `Filtered to “${term}”` : null, tag ? `${t('item.list.taggedWith')} ${tag}` : null, tagLine || null].filter(Boolean).join(' · ') || null;
  // 🔒 + New on an accounting document opens its own page in edit mode on a Draft, never a modal (decision 0409); the registry says which (T180).
  // 🔒 + New from a filtered list carries the filter, so Record Payment opens on the direction of the list it was pressed from (decision 0762).
  const startNew = onNew ?? (spec.create === 'page' ? () => navigate(`${spec.route}/${NEW_DOCUMENT_ID}${urlFilterQuery}`) : () => setCreating(true));
  // 🔒 Nothing to open, no button: a caller's own handler, or a create the register names, is what makes it drawable (piece 13).
  // 🔒 An accounting document's list answers whether this viewer could write one at all, and + New is withheld where it could not (decision 0687).
  const canCreate = (Boolean(onNew) || spec.create !== 'none') && actions?.create !== false;
  const newAction = canCreate
    ? <Button size={frame === 'pane' ? Size.Small : Size.Medium} color={Color.Navy} text={[`+ New ${spec.singular}`]} actions={[startNew]} />
    : undefined;
  /* 🔨 The collapse control is pinned in the pane's head, the way the navigation bar pins its own (piece 12; decision 0408). */
  const paneControls = frame === 'pane' && onCollapsePane ? (
    <button type="button" className={paneStyles.paneCollapse} onClick={onCollapsePane} data-list-pane-collapse="true" aria-label={t('listDetail.collapse')} title={t('listDetail.collapse')}>
      <Icon meaning="collapsePanel" size="nav" />
    </button>
  ) : null;
  // 🔒 A refused read is a refusal, not a failure: an external session reaching a list the server will not answer reads Access Denied rather than a retry it can never win (decision 0607).
  // 🔒 Beside a record the viewer opened, the refused pane draws nothing at all, and the frame withholds it (T218; decision 0693).
  if (paneRefused) return null;
  if (denied) return <AccessDenied />;
  const Frame = frame === 'pane' ? PaneFrame : PageModule;
  return (
    <Frame
      icon={RECORD_ICONS[spec.recordType] ?? null}
      title={title}
      subtitle={subtitle}
      newAction={newAction}
      controls={paneControls}
      {...(frame === 'pane' ? {} : { gear })}
    >
      {/* 🔨 One scrolling element in this column, and it is the List Viewer's rows (T160): the body fills the region and the card takes what the action rows leave. */}
      <div className={listStyles.body} data-list-body={frame}>
      {/* 🔒 The Items list's one selection action is Edit Selected in the action bar, its two accounts among its fields; Change Accounts is gone (decision 2890).
          Off-default accounts is a view, in the bar and the switcher, never a checkbox (decisions 0212, 2880). Import price list is a Settings entry (decision 0342). */}
      {/* The page's own declared actions (decision 0341), each drawn only where the API's gate answers true for this viewer. */}
      {offersExport || (spec.actions && actions && spec.actions.some((action) => actions[action.key])) ? (
        <div data-list-actions={spec.recordType} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          {(spec.actions && actions ? spec.actions.filter((action) => actions[action.key]) : []).map((action) => (
            action.key === 'issueStock'
              ? <Button key={action.key} size={Size.Small} text={[t('stock.issue.action')]} actions={[() => setIssuing(true)]} />
              : action.key === 'returnStock'
              ? <Button key={action.key} size={Size.Small} text={[t('stock.return.action')]} actions={[() => setReturning(true)]} />
              : <Button key={action.key} size={Size.Small} text={[t('vcard.action')]} actions={[() => setImportingVcards(true)]} />
          ))}
          {/* 🔒 Export sits with the list's other controls and writes the filtered rows and the visible columns (decision 10940). */}
          {offersExport ? <Button size={Size.Small} text={[t('list.export.action')]} actions={[() => void exportList()]} /> : null}
          {/* 🔒 Import Lookup Bundle sits with the Items list's own controls for a writer of Items (decision 12661): it stages a bundle and opens its Step Through. */}
          {offersLookupBundle ? <ImportLookupBundleButton apiBaseUrl={apiBaseUrl} onDone={(wrote) => { if (wrote) reload(); }} /> : null}
        </div>
      ) : null}
      {/* 🔒 The quick views: the person's starred views, built-in or own, in the queue bar's shape (decisions 1486, 2880). A list whose
          only starred view is All draws no bar, a single starred view other than All draws it (T2191), and a link out to another
          screen stands at the bar's end (piece 20). */}
      {store.quick.some((view) => view.id !== ALL_VIEW_ID) || (spec.barLinks?.length ?? 0) > 0 ? (
        <QuickViewBar
          name={spec.recordType}
          views={store.quick}
          currentId={store.currentId}
          onChoose={store.choose}
          links={(spec.barLinks ?? []).map((link) => ({ label: t(link.labelKey), route: link.route }))}
          onRoute={(route) => navigate(route)}
        />
      ) : null}
      {layouts.length > 1 ? (
        <div data-list-view-toggle={view} style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <Button size={Size.Small} color={view === 'list' ? Color.Navy : Color.Inactive} text={[t('list.layout.list')]} actions={[() => chooseView('list')]} />
          {boardable ? <Button size={Size.Small} color={view === 'board' ? Color.Navy : Color.Inactive} text={[t('deal.action.board')]} actions={[() => chooseView('board')]} /> : null}
          {mappable ? <Button size={Size.Small} color={view === 'map' ? Color.Navy : Color.Inactive} text={[t('list.layout.map')]} actions={[() => chooseView('map')]} /> : null}
        </div>
      ) : null}
      {segmentFilter && frame === 'page' && view === 'list' ? (
        <div className={listStyles.segments} data-list-segments={segmentFilter.param}>
          <SegmentedTab
            name={`${spec.recordType}-${segmentFilter.param}`}
            legend={segmentFilter.legend}
            value={segmentValue}
            onChange={writeSegment}
            segments={[{ value: '', label: t('list.segments.all') }, ...(segmentOptions.state.kind === 'ready' ? segmentOptions.state.data.rows ?? [] : []).map((row) => ({ value: row.label, label: row.label }))]}
          />
        </div>
      ) : null}
      {notice ? <AlertBanner tone="good" message={notice} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
      {mergeNotice ? <AlertBanner tone={mergeNotice.tone} message={mergeNotice.text} action={{ label: t('modal.dismiss'), onAction: () => setMergeNotice(null) }} /> : null}
      {exportRefusal ? <AlertBanner tone="error" message={exportRefusal} action={{ label: t('modal.dismiss'), onAction: () => setExportRefusal(null) }} /> : null}
      {mergeRefusal ? <AlertBanner tone="error" message={mergeRefusal} action={{ label: t('modal.dismiss'), onAction: () => setMergeRefusal(null) }} /> : null}
      {/* 🔒 The texted-card offers stand above the Leads list on its own page (decision 5180); a made Lead opens as + New Lead's does, a made Person opens its page. */}
      {spec.recordType === 'Lead' && frame === 'page' ? (
        <TextedCardOffers apiBaseUrl={apiBaseUrl} onCreated={(made) => (made.recordType === 'Lead' ? opened({ id: made.id }) : navigate(recordHref('PersonRecord', made.id)))} />
      ) : null}
      {boardable && view === 'board' ? <PipelineBoard apiBaseUrl={apiBaseUrl} term={term} criteria={viewCriteria} /> : isMappable(spec.recordType) && view === 'map' ? (
        <MapView recordType={spec.recordType} apiBaseUrl={apiBaseUrl} term={term} />
      ) : (
      <ListViewer
        recordType={spec.label}
        imageBaseUrl={apiBaseUrl}
        views={views}
        currentViewId={store.currentId}
        onViewChange={store.choose}
        onNewView={store.openNew}
        onEditView={store.openEdit}
        onStarView={(id) => void store.star(id)}
        viewNotice={store.starNotice}
        recordSingular={spec.singular}
        columns={columns}
        rows={rows}
        total={state.kind === 'ready' ? state.data.total ?? rows.length : 0}
        serverPage={{ page: state.kind === 'ready' ? state.data.page ?? page : page, onPageChange: (next) => setPaged({ scope: readKey, page: next }), scope: `${spec.recordType}?${selectionQuery}` }}
        idOf={(row) => String(row.id)}
        read={(row, key) => row[key]}
        sort={sort}
        onSortChange={setSort}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        state={viewerState}
        status={status}
        emptyAction={spec.emptyAction}
        {...(filterActive ? { filterNoMatch: { onClear: clearFilters, searchOnly: conditions.length === 0 && seriesChoice.series === '' && seriesChoice.manufacturer === '' } } : {})}
        onRetry={reload}
        // 🔒 The pane already heads the list, so the viewer draws no second band naming the type (decision 1444; T691).
        bandless={frame === 'pane'}
        // 🔒 T3028, T3066, decision 9720 (T3164): the Marks column, first after the checkbox, draws the gold dot and a missing tax form's triangle off the row's own fields.
        {...(spec.recordType === 'PersonRecord' || spec.recordType === 'CompanyRecord' ? {
          cellMarker: (row: Record<string, unknown>, key: string) => (key === 'marks' ? <ListRowMarks row={row} subject={spec.recordType === 'PersonRecord' ? 'person' : 'company'} onOpenTile={(tile) => openAtTile(row, tile)} /> : null),
        } : {})}
        // 🔒 A list whose spec pins Compact draws the compact rows at every width (decision 2852; T2059).
        {...(spec.layout ? { layout: spec.layout } : {})}
        {...(tagFiltered && frame === 'page' ? { filters: <TagFilterMenu groups={tagGroups} value={tagFilter} onChange={(next) => writeTagFilter(formatTagFilter(next))} /> } : seriesFiltered && frame === 'page' ? {
          filters: (
            <>
              <ItemSeriesFilter options={seriesOptions.state.kind === 'ready' ? seriesOptions.state.data.rows ?? [] : []} value={seriesChoice} onChange={writeSeries} />
              {/* 🔒 The Filter beside the Series menu (decision 8800): several conditions at once, an attribute's value among them. */}
              {conditionsFiltered ? (
                <ItemFilter
                  conditions={conditions}
                  columns={spec.columns}
                  attributes={criteriaAttributes}
                  plural={spec.label}
                  open={filtering}
                  onOpen={() => setFiltering(true)}
                  onClose={() => setFiltering(false)}
                  onApply={writeConditions}
                  onSaveAsView={(rows) => store.openNew({ criteria: rows })}
                />
              ) : null}
            </>
          ),
        } : {})}
        // 🔒 A row carries no Open where its list names no record page (decision 1158; T379, P24-9); every list names one now (decision 7580).
        {...(spec.openable === false ? {} : { onNavigate: open })}
        onRoute={(route) => navigate(route)}
        activeId={activeId}
        onScrollTop={keepScroll}
        // The place is read as the viewer is drawn new and only then, so a ref the scroll writes without a render is the right holder (decision 7750).
        // eslint-disable-next-line react-hooks/refs
        initialScrollTop={scrollTop.current}
        {...(spec.bulk || offersBulkEdit || offersMerge || offersLookupRequest ? { onSelectionChange: setSelected, onSelection: keepSelection } : {})}
        {...(offersBulkEdit || offersMerge || offersLookupRequest ? {
          selectionActions: (
            <>
              {/* 🔒 Edit Selected has no filter form yet, so over Select all N matching it stands disabled with its reason (decision 7780). */}
              {offersBulkEdit ? <Button size={Size.Small} color={Color.Navy} text={[t('bulkEdit.action')]} actions={[() => setBulkEditing(true)]} disabled={selectedCount === 0} /> : null}
              {/* 🔒 Merge stands only while two or more rows are selected, and above the cap it is disabled with its reason (decisions 7450, 7780). */}
              {offersMerge && selectedCount >= 2 ? (
                <Button size={Size.Small} color={Color.Navy} text={[t('duplicates.action.merge')]} actions={[() => void startMerge()]} disabled={selectedCount > MERGE_SET_CAP || resolvingMerge} {...(selectedCount > MERGE_SET_CAP ? { disabledReason: mergeSetCapSentence() } : {})} />
              ) : null}
              {/* 🔒 Export Lookup Request names the ticked Items for a person or an outside agent to fill as a lookup bundle (decision 12660). */}
              {offersLookupRequest ? <ExportLookupRequestButton selectedCount={selectedCount} resolve={resolveSelected} apiBaseUrl={apiBaseUrl} onSaved={setNotice} onRefused={setExportRefusal} /> : null}
            </>
          ),
        } : {})}
      />
      )}
      {/* 🔒 The list's Merge window (decision 7450), mounted only while open, so each opening reads its selection afresh. */}
      {merging && mergeType ? (
        <MergeSetWindow recordType={mergeType} ids={merging} apiBaseUrl={apiBaseUrl} onCancel={() => setMerging(null)} onMerged={(answer) => { setMerging(null); setMergeNotice(mergeSetNotice(answer)); reload(); }} />
      ) : null}
      {/* Mounted only while open, so a second import starts from a clean picker rather than the last file's preview. */}
      {importingVcards ? <VcardImportModal open apiBaseUrl={apiBaseUrl} onCancel={() => setImportingVcards(false)} onImported={(report) => { setNotice(`${report.created} ${t('vcard.report.created')} · ${report.updated} ${t('vcard.report.updated')} · ${report.skipped} ${t('vcard.report.skipped')}`); reload(); }} /> : null}
      {/* 🔒 Edit selected (decision 0982): mounted only while open, so each opening starts from the first field and no value. */}
      {bulkEditing && isBulkEditRecordType(spec.recordType) ? (
        <BulkEditModal open recordType={spec.recordType} fields={spec.bulkEdit ?? []} ids={selected} selection={bulkSelection} apiBaseUrl={apiBaseUrl} onCancel={() => setBulkEditing(false)} onApplied={(answer) => { setNotice(`${answer.accepted} ${t('bulkEdit.notice')}`); reload(); }} />
      ) : null}
      {/* The New View Modal as Add New View and Edit View (decisions 2170, 2175); the view it saves is this
          viewer's own preference row, and the list's first column can never be removed from it. */}
      <SavedViewWindow
        store={store}
        columns={spec.columns}
        plural={spec.label}
        sort={sort}
        pageSize={pageSize}
        {...(tagFiltered ? { tagFilter: { value: isEmptyTagFilter(tagFilter) ? null : formatTagFilter(tagFilter), summary: tagLine || null, summaryOf: (value: string | null) => tagFilterSummary(tagFilterGroupsFor(tagOptions, spec.recordType, parseTagFilter(value)), parseTagFilter(value)) || null } } : {})}
        // 🔒 On the Items list the view's rows offer the attribute types too, and a view saved from the Filter takes its conditions over (decision 8800).
        {...(conditionsFiltered ? { attributes: criteriaAttributes, onSeededSave: () => writeConditions([]) } : {})}
      />
      {/* 🔨 A list in a pane keeps its view under its own key, so the page's own list and the pane never share one (T1411). */}
      <SavedViewAddress value={store.addressed} onChange={store.setAddressed} {...(frame === 'pane' ? { param: SAVED_VIEW_PANE_PARAM } : {})} />
      {/* 🔨 A Site is created through its own combo modal, which writes the
          building, the site, its roles and the first note together; from this
          list there is no company yet, so the modal collects one first. */}
      {issuing ? <IssueStockModal apiBaseUrl={apiBaseUrl} onCancel={() => setIssuing(false)} onIssued={(issued) => { setIssuing(false); setNotice(`${t('stock.issue.done')} ${issued.onHandAfter} of ${issued.sku} at ${issued.siteName}.`); reload(); }} /> : null}
      {/* 🔒 Return Stock beside Issue Stock (decision 1609), unmounted when it closes for the same reason. */}
      {returning ? <ReturnStockModal apiBaseUrl={apiBaseUrl} onCancel={() => setReturning(false)} onReturned={(back) => { setReturning(false); setNotice(`${t('stock.return.done')} ${back.onHandAfter} of ${back.sku} at ${back.siteName}.`); reload(); }} /> : null}
      {spec.create === 'siteCombo' ? (
        /* 🔒 Unmounted when it closes, so Cancel drops the draft and the next + New Site opens clean (decision 3063; T2250). */
        creating ? <SiteModal open apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onCreated={(made) => opened({ id: made.siteId })} /> : null
      ) : spec.create === 'stockRevaluation' ? (
        /* 🔨 A revaluation posts as it is saved, so the modal's confirm is the posting and there is no draft behind it (decision 0441).
           🔨 It is unmounted rather than hidden: the modal holds the item, site, quantity, costs and reason in its own state, and
           the list stays mounted behind it, so a hidden one reopened pre-filled with what was just posted (found in review, 2026-09-16). */
        creating ? <NewRevaluationModal open apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onPosted={(id) => opened({ id })} /> : null
      ) : spec.create === 'vendorCredit' ? (
        /* 🔨 The raise returns to the list, where the new credit's row opens its page (decisions 0449, 7580).
           🔨 It is unmounted rather than hidden, for the reason the revaluation's modal is. */
        creating ? <VendorCreditModal open apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); reload(); }} /> : null
      ) : spec.create === 'bom' ? (
        /* 🔒 A bill of materials is made from its own list with a name and a description, attached to no Deal (decision 1092).
           🔨 Unmounted rather than hidden, for the reason the revaluation's modal is. It opens in its editor, where its lines are typed. */
        creating ? <NewBomModal apiBaseUrl={apiBaseUrl} onCancel={() => setCreating(false)} onCreated={(made) => { setCreating(false); reload(); navigate(`${recordHref(spec.recordType, made.id)}?mode=edit`); }} /> : null
      ) : spec.create === 'fields' ? (
        <NewRecordModal open={creating} recordType={spec.recordType} apiBaseUrl={apiBaseUrl} openedFrom="new" onCancel={() => setCreating(false)} onCreated={opened} />
      ) : (
        /* 🔨 An accounting document and a stock document with lines are created on their own page, never in a modal (decisions 0409, 0441); a record raised from another document offers nothing here. */
        null
      )}
      </div>
    </Frame>
  );
}

/**
 * 🔒 The columns a list draws as a pane (decisions 0318, 0639): the first two,
 * which identify the row, then the first status column and the first value
 * column where the list has them, in the order the register declares them. The
 * product owner, 2026-09-20: *"Where is the status pills on the accounting items
 * in the list on left when selected"*.
 */
export function paneColumns(allColumns: readonly ColumnDef[]): ColumnDef[] {
  // 🔒 A picture column is text-free and a pane is a compact head, so the pane leaves it out (T3333).
  const columns = allColumns.filter((column) => column.type !== 'image');
  const status = columns.find((column) => column.role === 'status');
  const value = columns.find((column) => column.role === 'value');
  // 🔒 The Marks column stays in the pane, at its far left, and is not one of the identifying pair (decision 9720; T3164).
  const marks = columns.find((column) => column.type === 'marks');
  const identifying = columns.filter((column) => column.type !== 'marks').slice(0, 2);
  const kept = new Set<ColumnDef>([...(marks ? [marks] : []), ...identifying, ...(status ? [status] : []), ...(value ? [value] : [])]);
  return columns.filter((column) => kept.has(column));
}

/** The list as a pane: a compact head with the title, the collapse control and the New button, then the rows, inside a card rather than a page module (decisions 0318, 0408). A pane has no title bar of the page module's, so it takes no gear: the frame choice hands `gear` to the page module alone (decisions 2805, 2815). */
function PaneFrame({ title, subtitle, newAction, controls, children }: { icon?: IconMeaning | null; title: string; subtitle?: string | null; newAction?: ReactNode; controls?: ReactNode; children?: ReactNode }) {
  return (
    <section className={paneStyles.pane} data-list-pane="true" aria-label={title}>
      <header className={paneStyles.paneHead}>
        <span className={paneStyles.paneTitle}>
          <span>{title}</span>
          {subtitle ? <span className={paneStyles.paneSubtitle}>{subtitle}</span> : null}
        </span>
        {controls}
        {newAction}
      </header>
      <div className={paneStyles.paneBody}>{children}</div>
    </section>
  );
}

/** The route element: the record type comes from the path, never from a prop the router cannot set. */
export function ListPageRoute({ recordType, apiBaseUrl = '' }: { recordType: keyof typeof LIST_PAGES; apiBaseUrl?: string }) {
  return <ListPage spec={LIST_PAGES[recordType]} apiBaseUrl={apiBaseUrl} />;
}

/** Exported for the record page, which reads the same id parameter. */
export function useRecordId(): string {
  return useParams().id ?? '';
}
